import {parseInteger} from '@benchling/js-utils';
import React from 'react';

import {OverflowMenuOption} from 'ui-library/menus/OverflowMenu';

import getParameterNamesInSQLQueries from './data/getParameterNamesInSQLQueries';
import isDefaultSystemName, {DEFAULT_PARAMETER_NAME_REGEX} from './data/isDefaultSystemName';

export interface AddParameterInput {
  disabled: boolean;
  onAddParameter: (newParameterName: string) => void;
  sqlQuery: string;
}

function getNumericSuffixIfDefaultName(parameterName: string): number | null {
  // Match default names like 'parameter', 'parameter1', 'parameter2', etc.
  if (!isDefaultSystemName(parameterName)) {
    return null;
  }
  const match = parameterName.match(DEFAULT_PARAMETER_NAME_REGEX);
  const numericalSuffixString = match![1];
  if (numericalSuffixString == null) {
    // 'parameter' -> 0
    return 0;
  }
  // 'parameter1' -> 1
  return parseInteger(numericalSuffixString);
}

function getHighestNumericSuffixAmongDefaultNames(parameterNames: Array<string>): number | null {
  const numericSuffixes = parameterNames
    .map((name) => getNumericSuffixIfDefaultName(name))
    .filter((suffix): suffix is number => suffix != null);
  return numericSuffixes.length ? Math.max(...numericSuffixes) : null;
}

/**
 * Return the next default parameter name to insert into the query
 *   - If query contains no default parameters, return 'parameter'
 *   - If query contains parameters (like 'parameter2'), return the next one (like 'parameter3')
 *
 * This way, we always insert a new parameter instead of reusing a default parameter (like 'parameter', if
 * the user selects this menu option twice) that may already be there.
 */
function getNextDefaultName(sqlQuery: string): string {
  const parameterNames = getParameterNamesInSQLQueries([sqlQuery]);
  const highestSuffix = getHighestNumericSuffixAmongDefaultNames(parameterNames);
  return highestSuffix != null ? `parameter${highestSuffix + 1}` : 'parameter';
}

/**
 * Menu option that adds a new parameter named {{parameter(n)}}
 *
 * The parent component should decide where in the SQL query this parameter should be inserted. It should
 * make some update to the query, to help the user understand that they need to type it into their query and
 * run it to insert a parameter.
 */
export default function getAddParameterMenuOption({
  disabled,
  onAddParameter,
  sqlQuery,
}: AddParameterInput): OverflowMenuOption {
  function handleSelect(): void {
    onAddParameter(getNextDefaultName(sqlQuery));
  }
  return {
    contents: <span>Add parameter</span>,
    icon: 'compose',
    isDisabled: disabled,
    onSelect: handleSelect,
    tooltipTitle: 'Add parameter\n(\u2009{{name}} from SQL editor)',
  };
}
